//
// Copyright (c) 2019-Present Syncari - All rights reserved.
//
import React from 'react';
import { Divider, Icon, Tooltip } from 'antd';
import cx from 'classnames';
import { each, partition } from 'lodash';

import FieldTypeBadge from 'components/FieldTypeBadge';
import { PicklistValue } from 'components/inputs/types';
import { getTextWidth } from 'utils/GraphUtil';
import { copyStringToClipboard } from 'utils/StringUtil';

import { USER_INPUTED_TOKEN_ID } from '../../condition';
import { getDataTypeFromValue } from '../utils/fuzzyValueHelpers';
import { FuzzyDropdownProps } from '../types';
import FuzzyOption from './FuzzyOption';

/**
 * FuzzyDropdown component handles the dropdown content
 * Supports grouped options, fuzzy options, and user tokens
 */
const FuzzyDropdown: React.FC<FuzzyDropdownProps> = ({
  children,
  optionData,
  values: _values, // Kept for API compatibility but not used internally
  useDataTypeBadges,
  searchValue,
  selectedValue,
  expandedFuzzyOption,
  isTokenInput,
  defaultValue,
  onOptionSelect,
  onSliderChange,
}) => {
  // Build options list
  const getOptions = (): JSX.Element[] => {
    const hasGroups = !!(optionData && optionData[0]?.picklistGroup);
    const optionElements: JSX.Element[] = [];

    const [tokenDataArray, filteredDataArray] = partition(
      optionData,
      (data: PicklistValue) => data.id === USER_INPUTED_TOKEN_ID
    );
    const userTokenData = tokenDataArray[0];

    if (hasGroups) {
      // Handle grouped options
      const groups: Record<string, PicklistValue[]> = {};
      each(filteredDataArray, (val) => {
        const { picklistGroup } = val;
        if (!picklistGroup) {
          return;
        }
        if (!groups[picklistGroup]) {
          groups[picklistGroup] = [];
        }
        groups[picklistGroup].push({ ...val });
      });

      each(groups, (groupOptions, key) => {
        // Group header
        optionElements.push(
          <div key={`group-${key}`} className="fuzzy-select-option-group-title" data-testid={`group-${key}`}>
            {key}
          </div>
        );

        // Group items
        each(groupOptions, (groupOption) => {
          const { value, label } = groupOption;
          const title = `${label} (${key})`;
          const dataType = getDataTypeFromValue(groupOption);

          optionElements.push(
            <div
              key={value}
              className="fuzzy-select-option fuzzy-select-option-group-item"
              onClick={(e) => {
                e.preventDefault();
                onOptionSelect(groupOption, false);
              }}
              data-testid={`grouped-option-${value}`}>
              {dataType && useDataTypeBadges ? (
                <div className="fuzzy-select-option-content">
                  <FieldTypeBadge dataType={dataType} description={dataType} size="small" />
                  <span title={title}>{label}</span>
                </div>
              ) : (
                <span title={title}>{label}</span>
              )}
            </div>
          );
        });
      });
    } else {
      // Partition into fuzzy and non-fuzzy options
      const [fuzzyOptions, regularOptions] = partition(
        filteredDataArray,
        (data: PicklistValue) => 'hasFuzzyOption' in data
      );

      // Render regular options
      regularOptions.forEach((opt) => {
        const isExpanded = expandedFuzzyOption?.option?.value === opt.value && opt.hasFuzzyOption;
        const isSelected = selectedValue === opt.value;

        optionElements.push(
          <FuzzyOption
            key={opt.value}
            option={opt}
            isFuzzy={false}
            isExpanded={isExpanded as boolean}
            isSelected={isSelected}
            useDataTypeBadges={useDataTypeBadges}
            expandedFuzzyOption={expandedFuzzyOption}
            onSelect={onOptionSelect}
            onSliderChange={onSliderChange}
          />
        );
      });

      // Add divider and fuzzy options if there are fuzzy options
      if (regularOptions.length > 0 && fuzzyOptions.length > 0) {
        optionElements.push(<Divider key="fuzzy-divider" className="fuzzy-divider" />);
      }

      // Add each fuzzy option
      fuzzyOptions.forEach((opt) => {
        const isExpanded = expandedFuzzyOption?.option?.value === opt.value && opt.hasFuzzyOption;
        const isSelected = selectedValue === opt.value;

        optionElements.push(
          <FuzzyOption
            key={opt.value}
            option={opt}
            isFuzzy={true}
            isExpanded={isExpanded as boolean}
            isSelected={isSelected}
            useDataTypeBadges={useDataTypeBadges}
            expandedFuzzyOption={expandedFuzzyOption}
            onSelect={onOptionSelect}
            onSliderChange={onSliderChange}
          />
        );
      });
    }

    // Add user token option if present
    if (userTokenData) {
      const { value: tokenValue } = userTokenData;
      const isHidden = defaultValue !== tokenValue || searchValue === tokenValue;

      optionElements.push(
        <div
          key={USER_INPUTED_TOKEN_ID}
          className={cx(
            'fuzzy-select-option',
            'user-inputed-token-option',
            isHidden && 'user-inputed-token-option--hidden'
          )}
          onClick={(e) => {
            e.preventDefault();
            if (isTokenInput) {
              onOptionSelect(userTokenData, false);
            }
          }}
          style={{
            opacity: isTokenInput ? 1 : 0.5,
            cursor: isTokenInput ? 'pointer' : 'not-allowed',
          }}
          data-testid="user-token-option">
          {tokenValue}
          <Tooltip title={'This is a user entered token value.'}>
            <Icon type="info-circle" theme="filled" className="user-inputed-token-option__icon" />
          </Tooltip>
          <span className="user-inputed-token-option__copy">
            <Icon
              type="copy"
              theme="outlined"
              onClick={(e) => {
                e.stopPropagation();
                copyStringToClipboard(tokenValue);
              }}
            />
          </span>
          <Tooltip title={tokenValue}>
            <div style={{ width: getTextWidth(`{{${tokenValue}}}`) }} className="user-inputed-token-option__tooltip" />
          </Tooltip>
        </div>
      );
    }

    return optionElements;
  };

  // Dividers and group titles are structural - they should survive a search that keeps at least
  // one real option, but on their own they are not "results" (a lone divider left over from an
  // otherwise empty search used to suppress the "no matching options" message).
  const isStructural = (opt: JSX.Element) =>
    opt.key === 'fuzzy-divider' || !!opt.props.className?.includes('group-title');

  // Filter options based on search
  const filterOptions = (opts: JSX.Element[]): JSX.Element[] => {
    if (!searchValue) return opts;

    const filtered = opts.filter((opt) => {
      if (isStructural(opt)) {
        return true;
      }

      // Extract text content for filtering
      const getTextContent = (element: any): string => {
        if (typeof element === 'string') return element;
        if (Array.isArray(element)) return element.map(getTextContent).join(' ');

        // For FuzzyOption components, get the label from option prop
        if (element?.props?.option?.label) return element.props.option.label;
        if (element?.props?.option?.value) return element.props.option.value;

        if (element?.props?.children) return getTextContent(element.props.children);
        if (element?.props?.label) return element.props.label;
        return '';
      };

      const textContent = getTextContent(opt);
      return textContent.toLowerCase().includes(searchValue.toLowerCase());
    });

    return filtered.some((opt) => !isStructural(opt)) ? filtered : [];
  };

  const options = children ? [] : getOptions();
  const filteredOptions = filterOptions(options);

  return (
    <div className="fuzzy-select-dropdown" data-testid="fuzzy-dropdown">
      {filteredOptions.length > 0 ? (
        <div className="fuzzy-select-options-group">{filteredOptions}</div>
      ) : (
        <div className="fuzzy-select-no-results" data-testid="fuzzy-no-results">
          No matching options
        </div>
      )}
    </div>
  );
};

export default FuzzyDropdown;
